import { useEffect, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Calendar, User, FileText, Tag } from 'lucide-react'
import { api } from '../services/api'
import Input from './Input'
import CategorySelector from './CategorySelector'
import FeaturedImageUpload from './FeaturedImageUpload'
import { useContentMetaStore } from '../stores/contentMetaStore'
import { getEntityConfig } from '../config/entityConfigs'

const iconMap = {
  Calendar,
  User,
  FileText,
  Tag,
}

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

export default function ContentMetaSidebar({
  entity,
  entityType,
  isNew,
  locale = 'en',
}) {
  const config = getEntityConfig(entityType)
  const useStore = useContentMetaStore(entityType)

  const meta = useStore((state) => state.meta)
  const setMeta = useStore((state) => state.setMeta)
  const updateField = useStore((state) => state.updateField)

  const [showHomepageConfirm, setShowHomepageConfirm] = useState(false)
  const slugManuallyEdited = useRef(false)

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users')
      return response.data
    },
  })

  const { data: homepageData } = useQuery({
    queryKey: ['current-homepage'],
    queryFn: async () => {
      try {
        const response = await api.get('/pages', {
          params: { per_page: 1, homepage: true },
        })
        return response.data.data?.[0] || null
      } catch {
        return null
      }
    },
    enabled: entityType === 'page',
  })

  const users = usersData?.data || []

  useEffect(() => {
    if (entity && !isNew) {
      const translation = entity.translations?.find((t) => t.locale === locale)

      const featuredImage = entity.featured_image
        ? (typeof entity.featured_image === 'string' ? entity.featured_image : entity.featured_image?.url || '')
        : ''

      const metaData = {
        title: entity.title || translation?.title || '',
        slug: entity.slug || '',
        excerpt: translation?.excerpt || '',
        meta_description: translation?.meta_description || '',
        published_at: entity.published_at
          ? new Date(entity.published_at).toISOString().slice(0, 16)
          : '',
        author_id: entity.author?.id || '',
        featured_image: featuredImage,
        allow_comments:
          entity.allow_comments ?? config.meta.base.allow_comments,
        is_published: entity.is_published ?? false,
        categories: entity.categories || [],
      }

      if (config.meta.specific.is_homepage !== undefined) {
        metaData.is_homepage = entity.is_homepage ?? false
      }

      setMeta(metaData)
    }
  }, [entity, isNew, locale, setMeta, config])

  useEffect(() => {
    if (isNew && users.length > 0 && !meta.author_id) {
      updateField('author_id', users[0].id)
    }
  }, [isNew, users, meta.author_id, updateField])

  useEffect(() => {
    if (isNew) {
      slugManuallyEdited.current = false
    } else if (entity?.slug) {
      slugManuallyEdited.current = true
    }
  }, [isNew, entity?.id])

  const renderField = (field, index) => {
    if (field.type === 'divider') {
      return <div key={index} className="border-t pt-2" />
    }

    if (field.specific && config.meta.specific[field.name] === undefined) {
      return null
    }

    const Icon = field.icon ? iconMap[field.icon] : null
    const fieldValue = meta[field.name]

    switch (field.type) {
      case 'text':
        const handleTextChange = (e) => {
          const value = e.target.value
          updateField(field.name, value)
          if (field.name === 'title' && !slugManuallyEdited.current) {
            updateField('slug', slugify(value))
          }
          if (field.name === 'slug') {
            slugManuallyEdited.current = true
          }
        }
        return (
          <div key={field.name}>
            <label
              className={`${
                Icon ? 'flex items-center' : 'block'
              } text-sm font-medium text-gray-700 mb-1`}
            >
              {Icon && <Icon className="h-4 w-4 mr-1" />}
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              value={fieldValue}
              onChange={handleTextChange}
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        )

      case 'textarea':
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <textarea
              value={fieldValue}
              onChange={(e) => updateField(field.name, e.target.value)}
              placeholder={field.placeholder}
              rows={field.rows || 3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        )

      case 'select':
        if (field.dataSource === 'users') {
          return (
            <div key={field.name}>
              <label
                className={`${
                  Icon ? 'flex items-center' : 'block'
                } text-sm font-medium text-gray-700 mb-1`}
              >
                {Icon && <Icon className="h-4 w-4 mr-1" />}
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <select
                value={fieldValue}
                onChange={(e) => updateField(field.name, e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                required={field.required}
              >
                <option value="">Select {field.label}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          )
        }
        return null

      case 'datetime':
        return (
          <div key={field.name}>
            <label
              className={`${
                Icon ? 'flex items-center' : 'block'
              } text-sm font-medium text-gray-700 mb-1`}
            >
              {Icon && <Icon className="h-4 w-4 mr-1" />}
              {field.label}
            </label>
            <input
              type="datetime-local"
              value={fieldValue}
              onChange={(e) => updateField(field.name, e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        )

      case 'category-selector':
        return (
          <div key={field.name}>
            <label
              className={`${
                Icon ? 'flex items-center' : 'block'
              } text-sm font-medium text-gray-700 mb-2`}
            >
              {Icon && <Icon className="h-4 w-4 mr-1" />}
              {field.label}
            </label>
            <CategorySelector
              selectedCategories={fieldValue || []}
              onChange={(categories) => updateField(field.name, categories)}
              locale={locale}
            />
          </div>
        )

      case 'image':
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            <FeaturedImageUpload
              imageUrl={fieldValue || ''}
              onImageChange={(data) => updateField(field.name, typeof data === 'string' ? data : data?.url || '')}
            />
          </div>
        )

      case 'checkbox':
        const handleCheckboxChange = (checked, fieldName) => {
          if (
            fieldName === 'is_homepage' &&
            checked &&
            homepageData &&
            entity?.id !== homepageData.id
          ) {
            setShowHomepageConfirm(true)
          } else {
            updateField(fieldName, checked)
          }
        }

        return (
          <>
            <label key={field.name} className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={fieldValue || false}
                onChange={(e) =>
                  handleCheckboxChange(e.target.checked, field.name)
                }
                className="mr-2 rounded"
              />
              {field.label}
            </label>

            {showHomepageConfirm && field.name === 'is_homepage' && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md">
                  <h3 className="text-lg font-semibold mb-2">
                    Set as Homepage
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Another page "{homepageData?.title || 'Homepage'}" is
                    already set as homepage. Do you want to replace it with this
                    page?
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowHomepageConfirm(false)
                        updateField('is_homepage', false)
                      }}
                      className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setShowHomepageConfirm(false)
                        updateField('is_homepage', true)
                      }}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-4">
          {config.singularLabel} Settings
        </h2>

        <div className="space-y-4">
          {config.fields.sidebar.map((field, index) =>
            renderField(field, index)
          )}
        </div>
      </div>
    </div>
  )
}
